import type { CodeExecutionMeta, ToolCallDisplay  } from "./types";
import type { AgentBody, AgentTool } from "@/schemas/agent.schema";
import { ESCALATE_TO_HUMAN_TOOL_NAMES } from "./message-transforms";
import { canonicalInternalToolName } from "./tool-search";

// AI-1195: pure tool-family classification. Shape says the family, color only
// the state. Origin resolves from the thread agent_snapshot tools, falling
// back to the playground agent context only when no snapshot is available -
// the callers assemble that array, this module only classifies by name.

export const DISCOVERY_TOOL_NAMES = ["search_tools", "load_tool"] as const;
export type DiscoveryToolName = (typeof DISCOVERY_TOOL_NAMES)[number];

export const CALL_CONTROL_ACTIONS = ["end_dialog", "transfer_call", "send_dtmf", "receive_dtmf"] as const;
export type CallControlAction = (typeof CALL_CONTROL_ACTIONS)[number];

export const NATIVE_CANONICAL_NAMES = [
  "web_search",
  "web_fetch",
  "calculator",
  "current_datetime",
  "code_execution",
] as const;
export type NativeCanonicalName = (typeof NATIVE_CANONICAL_NAMES)[number];

type McpToolEntry = Extract<AgentTool, { type: "mcp" }>;
type ApiToolEntry = Extract<AgentTool, { type: "api" }>;
type RagToolEntry = Extract<AgentTool, { type: "rag" }>;
type KnowledgeToolEntry = Extract<AgentTool, { type: "knowledge" }>;

export type McpOrigin = {
  /** Stable server identity for grouping: connection, then URL, then name. */
  serverKey: string;
  serverName: McpToolEntry["tool"]["mcp_server"]["name"];
  serverHost?: string;
};
export type ApiOrigin = { host?: ApiToolEntry["tool"]["api"]["url"] };
export type KnowledgeOrigin = {
  collectionName: KnowledgeToolEntry["tool"]["collection_name"];
};
export type RagOrigin = {
  collectionName: RagToolEntry["tool"]["collection_name"];
  fileName: RagToolEntry["tool"]["filename"];
};

export type AgentToolFamily =
  | { kind: "agent_tool"; agentType: "mcp"; mcp: McpOrigin }
  | { kind: "agent_tool"; agentType: "api"; api: ApiOrigin }
  | { kind: "agent_tool"; agentType: "knowledge"; knowledge: KnowledgeOrigin }
  | { kind: "agent_tool"; agentType: "rag"; rag: RagOrigin }
  | { kind: "agent_tool"; agentType: "function_call" };

export type ToolFamily =
  | { kind: "discovery"; tool: DiscoveryToolName }
  | AgentToolFamily
  | { kind: "native"; canonical: NativeCanonicalName }
  | { kind: "call_control"; action: CallControlAction }
  | { kind: "escalation" }
  | { kind: "unknown"; name: string };

function isDiscoveryToolName(name: string): name is DiscoveryToolName {
  return (DISCOVERY_TOOL_NAMES as readonly string[]).includes(name);
}

function isCallControlAction(name: string): name is CallControlAction {
  return (CALL_CONTROL_ACTIONS as readonly string[]).includes(name);
}

function isNativeCanonicalName(name: string): name is NativeCanonicalName {
  return (NATIVE_CANONICAL_NAMES as readonly string[]).includes(name);
}

export function hostnameOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).hostname || undefined;
  } catch {
    return undefined;
  }
}

function findSnapshotTool(name: string, tools: AgentTool[]): AgentTool | undefined {
  const exact = tools.find(tool => tool.tool.name === name);
  if (exact) return exact;
  // A call recorded under a deprecated alias resolves against the snapshot
  // entry stored under the other spelling (browser <-> web_search).
  const canonical = canonicalInternalToolName(name);
  if (canonical === name) return undefined;
  return tools.find(tool => canonicalInternalToolName(tool.tool.name) === canonical);
}

function mcpOrigin(tool: McpToolEntry): McpOrigin {
  const server = tool.tool.mcp_server;
  const host = hostnameOf(server.url);
  const serverKey = server.connection_id ?? server.url ?? server.name;
  return host ? { serverKey, serverName: server.name, serverHost: host } : { serverKey, serverName: server.name };
}

/**
 * Classification rule, in order:
 * 1. search_tools / load_tool -> discovery (never in the snapshot).
 * 2. Tool found in the snapshot decides by its type; a user tool named
 *    end_dialog is an agent tool, not call control.
 * 3. function_call with a reserved system name -> call control / escalation.
 * 4. Absent from the snapshot: reserved names still map by name, otherwise unknown.
 */
export function classifyToolCall(name: string, tools: AgentTool[]): ToolFamily {
  if (isDiscoveryToolName(name)) return { kind: "discovery", tool: name };
  const found = findSnapshotTool(name, tools);
  return found ? classifySnapshotTool(name, found) : classifyByName(name);
}

/** Rule 2/3: the snapshot entry's type decides. */
function classifySnapshotTool(name: string, found: AgentTool): ToolFamily {
  switch (found.type) {
    case "api": {
      const host = hostnameOf(found.tool.api?.url);
      return { kind: "agent_tool", agentType: "api", api: host ? { host } : {} };
    }
    case "mcp":
      return { kind: "agent_tool", agentType: "mcp", mcp: mcpOrigin(found) };
    case "rag":
      return {
        kind: "agent_tool",
        agentType: "rag",
        rag: { collectionName: found.tool.collection_name, fileName: found.tool.filename },
      };
    case "knowledge":
      return {
        kind: "agent_tool",
        agentType: "knowledge",
        knowledge: { collectionName: found.tool.collection_name },
      };
    case "internal": {
      const canonical = canonicalInternalToolName(found.tool.name);
      return isNativeCanonicalName(canonical) ? { kind: "native", canonical } : { kind: "unknown", name };
    }
    case "function_call":
      if (isCallControlAction(name)) return { kind: "call_control", action: name };
      if (ESCALATE_TO_HUMAN_TOOL_NAMES.has(name)) return { kind: "escalation" };
      return { kind: "agent_tool", agentType: "function_call" };
  }
}

/** Rule 4: not in the snapshot, reserved names still map by name. */
function classifyByName(name: string): ToolFamily {
  if (isCallControlAction(name)) return { kind: "call_control", action: name };
  if (ESCALATE_TO_HUMAN_TOOL_NAMES.has(name)) return { kind: "escalation" };
  const canonical = canonicalInternalToolName(name);
  if (isNativeCanonicalName(canonical)) return { kind: "native", canonical };
  return { kind: "unknown", name };
}

// ---------------------------------------------------------------------------
// Discovery collapse: a search_tools followed by load_tool calls of the same
// assistant message renders as ONE line.
// ---------------------------------------------------------------------------

export interface DiscoveryGroup {
  search: ToolCallDisplay | null;
  query: string;
  found: string[];
  loads: ToolCallDisplay[];
  loadedNames: string[];
}

export type MessageDisplayItem =
  | { kind: "tool"; call: ToolCallDisplay }
  | { kind: "discovery"; group: DiscoveryGroup };

function parseJsonObject(text: string): Record<string, string> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
  // Only string fields are meaningful to the summaries; anything else is
  // dropped here so every consumer below works with narrowed types.
  const fields: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === "string") fields[key] = value;
  }
  return fields;
}

function stringField(obj: Record<string, string> | null, key: string): string | null {
  if (!obj) return null;
  const value = obj[key];
  return typeof value === "string" ? value : null;
}

/** search_tools output lists hits as "1. **name**: description" lines. */
export function parseSearchFound(output: string | undefined): string[] {
  if (!output) return [];
  const found: string[] = [];
  for (const match of output.matchAll(/\*\*([^*]+)\*\*/g)) {
    const name = match[1]?.trim();
    if (name) found.push(name);
  }
  return found;
}

function parseLoadName(call: ToolCallDisplay): string | null {
  const fromArgs = stringField(parseJsonObject(call.args), "tool_name")?.trim();
  if (fromArgs) return fromArgs;
  // `\S+` never captures an empty name, so a match is always usable.
  return /^##\s*(\S+)/m.exec(call.response ?? "")?.[1] ?? null;
}

function discoveryGroupForSearch(search: ToolCallDisplay, loads: ToolCallDisplay[]): DiscoveryGroup {
  const query = stringField(parseJsonObject(search.args), "query")?.trim() ?? "";
  return {
    search,
    query,
    found: parseSearchFound(search.response),
    loads,
    loadedNames: loads.map(parseLoadName).filter((name): name is string => !!name),
  };
}

/** The consecutive load_tool calls starting at `start`. */
function loadsFollowing(calls: ToolCallDisplay[], start: number): ToolCallDisplay[] {
  const loads: ToolCallDisplay[] = [];
  for (let next = start; next < calls.length; next++) {
    const following = calls[next];
    if (following?.name !== "load_tool") break;
    loads.push(following);
  }
  return loads;
}

/** A load_tool with no search before it: a trace line of its own. */
function discoveryGroupForLoad(call: ToolCallDisplay): DiscoveryGroup {
  const name = parseLoadName(call);
  return { search: null, query: "", found: [], loads: [call], loadedNames: name ? [name] : [] };
}

export function groupMessageTools(calls: ToolCallDisplay[]): MessageDisplayItem[] {
  const items: MessageDisplayItem[] = [];
  let index = 0;
  while (index < calls.length) {
    const call = calls[index];
    if (!call) {
      index++;
      continue;
    }
    if (call.name === "search_tools") {
      const loads = loadsFollowing(calls, index + 1);
      items.push({ kind: "discovery", group: discoveryGroupForSearch(call, loads) });
      index += 1 + loads.length;
      continue;
    }
    if (call.name === "load_tool") {
      items.push({ kind: "discovery", group: discoveryGroupForLoad(call) });
      index++;
      continue;
    }
    items.push({ kind: "tool", call });
    index++;
  }
  return items;
}

// ---------------------------------------------------------------------------
// Native summaries. Args arrive as JSON strings on ToolCallDisplay.args;
// everything is parsed at this boundary with type guards and degrades to
// null ("no summary") when args/output do not match - never throws.
// ---------------------------------------------------------------------------

export interface WebSearchResult { title: string; url: string; host: string }

export type NativeSummary =
  | { canonical: "web_search"; query: string; resultCount: number; results: WebSearchResult[] }
  | { canonical: "web_fetch"; url: string; host: string; sizeKB: number | null; title: string | null; excerpt: string | null }
  | { canonical: "calculator"; expression: string; result: string }
  | { canonical: "current_datetime"; timezone: string | null; datetime: string }
  | { canonical: "code_execution"; fileCount: number; preview: string | null };

function parseResultCount(output: string, fallback: number): number {
  const header = /Results:\s*(\d+)/.exec(output)?.[1];
  if (header !== undefined) {
    const count = Number.parseInt(header, 10);
    if (Number.isFinite(count)) return count;
  }
  return fallback;
}

export function parseWebResults(output: string): WebSearchResult[] {
  const results: WebSearchResult[] = [];
  const lines = output.split("\n");
  for (let i = 0; i < lines.length; i++) {
    // Prefix match + trim instead of `\s*(.+?)\s*$`: the lazy group between two
    // whitespace quantifiers backtracks super-linearly (Sonar S5852).
    const line = lines[i] ?? "";
    const prefix = /^\d+\./.exec(line);
    const title = prefix ? line.slice(prefix[0].length).trim() : "";
    if (!title) continue;
    let url = "";
    for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
      const candidate = lines[j]?.trim() ?? "";
      if (/^https?:\/\/\S+/.test(candidate)) {
        url = candidate.split(/\s/)[0] ?? "";
        break;
      }
      if (/^\d+\.\s/.test(candidate)) break;
    }
    if (!url) continue;
    results.push({ title, url, host: hostnameOf(url) ?? url });
  }
  return results;
}

function parseFetchTitle(output: string): string | null {
  const line = output.split("\n").find(l => l.startsWith("Title:"));
  const title = line?.slice("Title:".length).trim();
  // An empty title ("Title:" alone) is no title, so no `??` here.
  if (!title) return null;
  return title;
}

function parseFetchExcerpt(output: string): string | null {
  const body = output.split(/\n\s*\n/).slice(1).join("\n\n").trim();
  if (!body) return null;
  return body.slice(0, 300);
}

function summarizeCalculator(args: string, response: string | undefined): NativeSummary | null {
  let expression = "";
  const trimmed = args.trim();
  if (trimmed.startsWith("{")) {
    const obj = parseJsonObject(trimmed);
    expression = stringField(obj, "input") ?? stringField(obj, "expression") ?? "";
  } else if (trimmed.startsWith('"')) {
    try {
      const decoded: unknown = JSON.parse(trimmed);
      expression = typeof decoded === "string" ? decoded : "";
    } catch {
      expression = "";
    }
  } else {
    expression = trimmed;
  }
  expression = expression.trim();
  if (!expression) return null;
  return { canonical: "calculator", expression, result: (response ?? "").trim() };
}

const CODE_EXECUTION_PREVIEW_LENGTH = 120;

/** First non-empty line of the code result/stdout, for the zero-file summary. */
function codeExecutionPreview(meta: CodeExecutionMeta | undefined): string | null {
  if (!meta || meta.hasError) return null;
  const lines = `${meta.result ?? ""}\n${meta.stdout ?? ""}`.split("\n");
  const first = lines.map(line => line.trim()).find(line => line !== "");
  if (!first) return null;
  return first.length > CODE_EXECUTION_PREVIEW_LENGTH
    ? `${first.slice(0, CODE_EXECUTION_PREVIEW_LENGTH)}…`
    : first;
}

function summarizeWebSearch(args: string, response: string | undefined): NativeSummary | null {
  const query = stringField(parseJsonObject(args), "query")?.trim();
  if (!query) return null;
  if (!response) return { canonical: "web_search", query, resultCount: 0, results: [] };
  const results = parseWebResults(response);
  return { canonical: "web_search", query, resultCount: parseResultCount(response, results.length), results };
}

function summarizeWebFetch(args: string, response: string | undefined): NativeSummary | null {
  const url = stringField(parseJsonObject(args), "url")?.trim();
  if (!url) return null;
  const host = hostnameOf(url) ?? url;
  if (!response) return { canonical: "web_fetch", url, host, sizeKB: null, title: null, excerpt: null };
  return {
    canonical: "web_fetch",
    url,
    host,
    sizeKB: Math.round((response.length / 1024) * 10) / 10,
    title: parseFetchTitle(response),
    excerpt: parseFetchExcerpt(response),
  };
}

export function summarizeNative(
  canonical: NativeCanonicalName,
  args: string,
  response: string | undefined,
  codeExecution?: CodeExecutionMeta,
): NativeSummary | null {
  switch (canonical) {
    case "web_search":
      return summarizeWebSearch(args, response);
    case "web_fetch":
      return summarizeWebFetch(args, response);
    case "calculator":
      return summarizeCalculator(args, response);
    case "current_datetime": {
      const datetime = (response ?? "").trim();
      if (!datetime) return null;
      return { canonical, timezone: stringField(parseJsonObject(args), "timezone"), datetime };
    }
    case "code_execution":
      // The server tool is Python-only and its args carry just `code`.
      return { canonical, fileCount: codeExecution?.files?.length ?? 0, preview: codeExecutionPreview(codeExecution) };
  }
}

// ---------------------------------------------------------------------------
// Call-control summaries: the fields the event pill sentence renders from.
// The reason carries the raw response text; the pill only shows it in the
// error state decided by the caller from the tool status.
// ---------------------------------------------------------------------------

export type CallControlSummary =
  | { action: "receive_dtmf"; digits: string | null }
  | { action: "send_dtmf"; digits: string | null }
  | { action: "transfer_call"; destination: string | null; reason: string | null }
  | { action: "end_dialog"; reason: string | null };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseDigitsField(text: string | undefined): string | null {
  if (!text) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (isRecord(parsed) && typeof parsed.digits === "string" && parsed.digits) {
      return parsed.digits;
    }
  } catch {
    // Not JSON - fall through to the bare-digits check.
  }
  const trimmed = text.trim();
  return /^[0-9*#]+$/.test(trimmed) ? trimmed : null;
}

export function summarizeCallControl(
  action: CallControlAction,
  args: string,
  response: string | undefined,
): CallControlSummary {
  const obj = parseJsonObject(args);
  switch (action) {
    case "receive_dtmf":
      return { action, digits: parseDigitsField(response) };
    case "send_dtmf":
      return { action, digits: stringField(obj, "digits") ?? parseDigitsField(response) };
    case "transfer_call": {
      const reason = (response ?? "").trim();
      return { action, destination: stringField(obj, "destination"), reason: reason || null };
    }
    case "end_dialog": {
      const fromArgs = stringField(obj, "reason");
      const reason = fromArgs ?? (response ?? "").trim();
      return { action, reason: reason || null };
    }
  }
}

// ---------------------------------------------------------------------------
// Duration: tool response metadata.created_at minus assistant message
// created_at. Null when either timestamp is missing.
// ---------------------------------------------------------------------------

export function callDurationMs(start: Date | null, end: Date | null): number | null {
  if (!start || !end) return null;
  return Math.max(0, end.getTime() - start.getTime());
}

export function formatDurationLabel(ms: number | null): string | null {
  if (ms === null) return null;
  return `${(ms / 1000).toLocaleString("default", { maximumFractionDigits: 1 })} s`;
}

// ---------------------------------------------------------------------------
// Card shell helpers shared by the collapsible card and the static native
// card, so status presentation lives in one place.
// ---------------------------------------------------------------------------

export function toolStatusPresentation(status: ToolCallDisplay["status"]): { icon: string; colorClass: string } {
  switch (status) {
    case "pending":
      return { icon: "svg-spinners:ring-resize", colorClass: "text-blue-9" };
    case "completed":
      return { icon: "lucide:check-circle-2", colorClass: "text-green-9" };
    case "error":
      return { icon: "lucide:x-circle", colorClass: "text-red-9" };
  }
}

/** Manual function_call detection (the playground answer row), by entry. */
export function isManualToolCall(name: string, tools: AgentTool[]): boolean {
  if (name.startsWith("$")) return true;
  return tools.find(tool => tool.tool.name === name)?.type === "function_call";
}

/**
 * Origin lookup for one call: snapshot tools win; the playground agent is
 * only the fallback when no snapshot is available.
 */
export function resolveLookupTools(
  snapshotTools: AgentTool[] | null,
  agent?: AgentListSource,
): AgentTool[] {
  if (snapshotTools !== null) return snapshotTools;
  if (!agent) return [];
  return collectListTools(agent);
}

/** Call/escalation row titles shared by ToolGroupsList and AgentsTable. */
export const CALL_ROW_TITLE_KEYS = {
  end_dialog: "agents:tool_families.groups.call_end",
  transfer_call: "agents:tool_families.groups.call_transfer",
  send_dtmf: "agents:tool_families.groups.call_send_dtmf",
  receive_dtmf: "agents:tool_families.groups.call_receive_dtmf",
} as const;

export const ESCALATE_ROW_TITLE_KEY = "agents:tool_families.groups.escalate" as const;

// ---------------------------------------------------------------------------
// Tool lists (ThreadInfo, ConversationInfo, AgentsTable) grouped by the same
// families, reusing the classifier.
// ---------------------------------------------------------------------------

export type ToolListGroupKey = "agent" | "mcp" | "native" | "call_control" | "escalation";

export interface ToolListGroup {
  key: ToolListGroupKey;
  serverKey?: string;
  serverName?: string;
  serverHost?: string;
  tools: AgentTool[];
}

function groupKeyOf(family: ToolFamily): ToolListGroupKey {
  switch (family.kind) {
    case "agent_tool":
      return family.agentType === "mcp" ? "mcp" : "agent";
    case "native":
      return "native";
    case "call_control":
      return "call_control";
    case "escalation":
      return "escalation";
    case "discovery":
    case "unknown":
      return "agent";
  }
}

const TOOL_LIST_GROUP_ORDER: readonly ToolListGroupKey[] = ["agent", "mcp", "native", "call_control", "escalation"];

export function groupToolsForList(tools: AgentTool[]): ToolListGroup[] {
  const groups: ToolListGroup[] = [];
  const mcpByServer = new Map<string, ToolListGroup>();
  const byKey = new Map<ToolListGroupKey, ToolListGroup>();

  function groupFor(key: ToolListGroupKey): ToolListGroup {
    let group = byKey.get(key);
    if (!group) {
      group = { key, tools: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    return group;
  }

  for (const tool of tools) {
    const family = classifyListEntry(tool);
    const key = groupKeyOf(family);
    if (family.kind === "agent_tool" && family.agentType === "mcp") {
      const { serverKey, serverName, serverHost } = family.mcp;
      let group = mcpByServer.get(serverKey);
      if (!group) {
        group = { key, serverKey, serverName, serverHost, tools: [] };
        mcpByServer.set(serverKey, group);
        groups.push(group);
      }
      group.tools.push(tool);
      continue;
    }
    groupFor(key).tools.push(tool);
  }

  return groups.sort((a, b) => TOOL_LIST_GROUP_ORDER.indexOf(a.key) - TOOL_LIST_GROUP_ORDER.indexOf(b.key));
}

/**
 * Compact tool cell of the agents table: an MCP server shows up once, with
 * the tools it contributes, instead of flooding the row with every tool name.
 * Everything else stays one entry per tool, in the same group order as the
 * full grouped list.
 */
export type TableToolEntry
  = | { kind: "tool"; tool: AgentTool; family: ToolFamily }
    | { kind: "mcp_server"; serverKey: string; serverName: string; serverHost?: string; tools: AgentTool[] };

export function tableToolEntries(tools: AgentTool[]): TableToolEntry[] {
  return groupToolsForList(tools).flatMap((group): TableToolEntry[] => {
    if (group.key === "mcp" && group.serverKey !== undefined && group.serverName !== undefined) {
      return [{ kind: "mcp_server", serverKey: group.serverKey, serverName: group.serverName, serverHost: group.serverHost, tools: group.tools }];
    }
    return group.tools.map(tool => ({ kind: "tool", tool, family: classifyListEntry(tool) }));
  });
}

/**
 * Classify one concrete list entry by its own identity, never by name
 * against the whole array: two MCP servers exposing the same tool name keep
 * two groups, and a same-named API never steals an injected call tool.
 */
export function classifyListEntry(tool: AgentTool): ToolFamily {
  return classifyToolCall(tool.tool.name, [tool]);
}

export type AgentListSource = Pick<AgentBody, "tools" | "mcp_servers" | "call_configuration" | "chat_configuration">;

function enabledCallEntry(name: CallControlAction): AgentTool {
  return { type: "function_call", tool: { name, description: "", parameters: {} } };
}

/**
 * Assembles the classifiable tool array for agent-based surfaces (AgentsTable
 * and the ToolCallBlock playground fallback): agent tools plus MCP server
 * tools plus an entry per enabled call-control / escalation capability, so
 * the "Chamada" and "Atendimento humano" groups appear. Snapshot-based
 * lists pass snapshot.tools straight to groupToolsForList instead - the
 * server already injected those entries as function_call.
 */
export function collectListTools(agent: AgentListSource): AgentTool[] {
  const collected: AgentTool[] = [...(agent.tools ?? [])];

  for (const server of agent.mcp_servers ?? []) {
    if (server.disabled) continue;
    for (const tool of server.tools ?? []) {
      if (tool.disabled) continue;
      collected.push({
        type: "mcp",
        tool: {
          name: tool.name,
          description: tool.description ?? "",
          parameters: tool.parameters ?? {},
          mcp_server: { name: server.name, disabled: server.disabled, url: server.url, connection_id: server.connection_id },
        },
      });
    }
  }

  const callTools = agent.call_configuration?.tools;
  if (callTools?.end_dialog?.enabled) collected.push(enabledCallEntry("end_dialog"));
  if (callTools?.transfer_call?.enabled) collected.push(enabledCallEntry("transfer_call"));
  if (callTools?.send_dtmf?.enabled) collected.push(enabledCallEntry("send_dtmf"));
  if (callTools?.receive_dtmf?.enabled) collected.push(enabledCallEntry("receive_dtmf"));
  if (agent.chat_configuration?.tools?.escalate_to_human?.enabled) {
    collected.push({ type: "function_call", tool: { name: "escalate_to_human", description: "", parameters: {} } });
  }

  return collected;
}
